import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getDefaultCustomerId } from '../auth/permissions';
import { getCustomerPortalCustomers } from '../api/customerPortalApi';

const CustomerPortalContext = createContext(undefined);

export function CustomerPortalProvider({ children }) {
  const { session } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCustomerId, setActiveCustomerId] = useState(null);

  useEffect(() => {
    if (!session) {
      setCustomers([]);
      setActiveCustomerId(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    getCustomerPortalCustomers()
      .then((response) => {
        const rows = response.data || [];
        setCustomers(rows);
        const defaultId = getDefaultCustomerId(session) || rows[0]?.id || null;
        setActiveCustomerId(defaultId);
      })
      .catch(() => {
        setCustomers([]);
        setActiveCustomerId(getDefaultCustomerId(session));
      })
      .finally(() => setLoading(false));
  }, [session]);

  const activeCustomer = customers.find((c) => c.id === activeCustomerId) || null;

  const value = useMemo(
    () => ({
      customers,
      activeCustomer,
      activeCustomerId,
      setActiveCustomerId,
      loading,
    }),
    [customers, activeCustomer, activeCustomerId, loading],
  );

  return (
    <CustomerPortalContext.Provider value={value}>
      {children}
    </CustomerPortalContext.Provider>
  );
}

export function useCustomerPortal() {
  const context = useContext(CustomerPortalContext);

  if (!context) {
    throw new Error('useCustomerPortal must be used within CustomerPortalProvider');
  }

  return context;
}
